import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Menu, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BRAND } from "@/lib/brand";
import { useMobilI18n } from "@/lib/mobilI18n";

function LangSwitcher({ scrolled }) {
  const { locale, setLocale, locales } = useI18n();
  return (
    <div className="flex items-center gap-1 text-[11px] uppercase tracking-[0.22em]" data-testid="lang-switcher">
      {Object.keys(locales).map((lc, idx) => (
        <span key={lc} className="flex items-center">
          <button
            onClick={() => setLocale(lc)}
            data-testid={`lang-${lc}`}
            className={`transition-colors px-1 ${
              locale === lc
                ? "text-[#E2C9A0] font-medium"
                : scrolled
                ? "text-[#fdfcf9]/65 hover:text-[#fdfcf9]"
                : "text-white/70 hover:text-white"
            }`}
          >
            {locales[lc]}
          </button>
          {idx < Object.keys(locales).length - 1 && (
            <span className={scrolled ? "text-[#fdfcf9]/35" : "text-white/35"}>·</span>
          )}
        </span>
      ))}
    </div>
  );
}

function FeaturedLink({ featured, mobile, onClick }) {
  return (
    <Link
      to={featured.to}
      onClick={onClick}
      data-testid={mobile ? "nav-mobile-featured" : "nav-featured"}
      className={
        mobile
          ? "text-[#E2C9A0] text-base tracking-[0.18em] uppercase font-medium py-3 border-b border-[#fdfcf9]/10"
          : "text-[11px] leading-[1.2] uppercase tracking-[0.1em] font-semibold max-w-[180px] text-[#E2C9A0] hover:text-[#fdfcf9] transition-colors border-l border-[#E2C9A0]/40 pl-4"
      }
    >
      {featured.label}
    </Link>
  );
}

export default function Navbar({ links: customLinks, featured: customFeatured, cta, logoHref = "#top" }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useI18n();
  const { m } = useMobilI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const links = customLinks || [
    { id: "sala", label: t.nav.sala },
    { id: "eventos", label: t.nav.eventos },
    { id: "disponibilidad", label: t.nav.disponibilidad },
    { id: "tarifas", label: t.nav.tarifas },
    { id: "configuraciones", label: t.nav.configuraciones },
    { id: "galeria", label: t.nav.galeria },
    { id: "contacto", label: t.nav.contacto },
  ];
  const featured = customFeatured || { to: "/eventos-externos", label: m.navLabel };
  const ctaLink = cta || { href: "#disponibilidad", label: t.nav.reserveCTA };
  const LogoTag = logoHref.startsWith("/") ? Link : "a";
  const logoProps = logoHref.startsWith("/") ? { to: logoHref } : { href: logoHref };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || menuOpen ? "bg-[#3e664c] shadow-[0_8px_25px_rgb(31,58,48,0.25)]" : "bg-transparent"
      }`}
    >
      <div className={`w-full border-b transition-colors ${scrolled || menuOpen ? "border-[#fdfcf9]/10" : "border-white/10"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-1.5 flex items-center justify-end">
          <LangSwitcher scrolled={scrolled || menuOpen} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-4">
        <LogoTag {...logoProps} data-testid="nav-logo" className="flex items-center gap-2 shrink-0" onClick={() => setMenuOpen(false)}>
          <img
            src={BRAND.logoUrl}
            alt="Aura"
            className="h-8 md:h-9 w-auto brightness-0 invert shrink-0"
            style={{ minHeight: "2rem" }}
          />
        </LogoTag>
        <nav className="hidden xl:flex items-center gap-4 shrink min-w-0">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className={`text-sm tracking-wide transition-colors whitespace-nowrap ${
                scrolled ? "text-[#fdfcf9]/85 hover:text-[#E2C9A0]" : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
          <FeaturedLink featured={featured} />
        </nav>
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <a
            href={BRAND.instagramUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-instagram"
            aria-label="Instagram Aura Sant Cugat"
            className={`hidden sm:inline-flex xl:hidden w-9 h-9 items-center justify-center rounded-full transition-all shrink-0 ${
              scrolled
                ? "text-[#fdfcf9]/85 hover:text-[#E2C9A0] hover:bg-[#fdfcf9]/10"
                : "text-white/85 hover:text-white hover:bg-white/10"
            }`}
          >
            <Instagram size={18} />
          </a>
          <a
            href={ctaLink.href}
            data-testid="nav-cta-reserve"
            onClick={() => setMenuOpen(false)}
            className={`hidden sm:inline-flex items-center gap-2 px-4 md:px-5 py-2 rounded-full text-xs uppercase tracking-[0.16em] whitespace-nowrap transition-all shrink-0 ${
              scrolled
                ? "bg-[#E2C9A0] text-[#2e5949] hover:bg-[#fdfcf9]"
                : "bg-[#fdfcf9] text-[#3e664c] hover:bg-[#E2C9A0]"
            }`}
          >
            {ctaLink.label}
          </a>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className={`xl:hidden w-10 h-10 inline-flex items-center justify-center rounded-full transition-colors ${
              scrolled || menuOpen
                ? "text-[#fdfcf9] hover:bg-[#fdfcf9]/10"
                : "text-white hover:bg-white/10"
            }`}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          className="xl:hidden fixed inset-x-0 top-[104px] bottom-0 bg-[#3e664c] overflow-y-auto"
          data-testid="nav-mobile-drawer"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setMenuOpen(false)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="text-[#fdfcf9] text-base tracking-wide py-3 border-b border-[#fdfcf9]/10 hover:text-[#E2C9A0] transition-colors"
              >
                {l.label}
              </a>
            ))}
            <FeaturedLink featured={featured} mobile onClick={() => setMenuOpen(false)} />
            <div className="mt-6 flex flex-col gap-3">
              <a
                href={ctaLink.href}
                onClick={() => setMenuOpen(false)}
                data-testid="nav-mobile-cta"
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#E2C9A0] text-[#2e5949] text-xs uppercase tracking-[0.18em] font-medium"
              >
                {ctaLink.label}
              </a>
              <a
                href={BRAND.instagramUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="nav-mobile-instagram"
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-[#fdfcf9]/40 text-[#fdfcf9] text-xs uppercase tracking-[0.18em]"
              >
                <Instagram size={16} /> Instagram
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
